import clsx from 'clsx';
import {
  AttachmentPreviewList,
  SendButton as DefaultSendButton,
  SimpleAttachmentSelector,
  TextareaComposer,
  useComponentContext,
  useMessageComposer,
  useMessageInputContext,
  WithDragAndDropUpload,
} from 'stream-chat-react';

import { AtMentionButton } from 'src/components/chat/inner-channel/custom-message-input/ActionButtons';
import { useMessageInputDropZone } from 'src/components/chat/inner-channel/custom-message-input/useMessageInputDropZone';
import { useInternalChatContext } from 'src/features/assistant/internalChatContext';
import { useOSDetector } from 'src/hooks/useOSDetector';

// created while referencing the default MessageInputFlat component
// https://github.com/GetStream/stream-chat-react/blob/master/src/components/MessageInput/MessageInputFlat.tsx
export const CustomMessageInput = () => {
  const { handleSubmit, textareaRef } = useMessageInputContext();
  const { textComposer } = useMessageComposer();
  const { SendButton = DefaultSendButton } = useComponentContext();
  const { isOSDeleteKeyPressed } = useOSDetector();
  const handleOnKeyDownCapture = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (isOSDeleteKeyPressed(event)) {
      event.stopPropagation();
    }
  };
  const { isInternalChat } = useInternalChatContext();
  const { messageInputRef } = useMessageInputDropZone({ isInternalChat });

  return (
    <WithDragAndDropUpload>
      <div
        ref={messageInputRef}
        className={clsx(
          'bg-inherit py-3',
          isInternalChat ? 'px-4' : 'pl-4 pr-9',
        )}
      >
        <div
          className="border border-solid border-non-hover-border active:border-black-heading focus-within:border-black-heading rounded-xl px-3 py-2 cursor-text"
          role="textbox"
          tabIndex={0}
          onClick={() => textareaRef.current?.focus()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && event.target !== textareaRef.current) {
              event.preventDefault();
              textareaRef.current?.focus();
            }
          }}
        >
          <AttachmentPreviewList />
          <TextareaComposer
            className="w-full border-none resize-none overflow-x-hidden p-0 mb-2 text-base placeholder-light-gray bg-inherit"
            containerClassName="relative" // this allows the suggestion list to be correctly positioned
            name="message input"
            onKeyDownCapture={handleOnKeyDownCapture}
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {isInternalChat && (
                <AtMentionButton
                  onClick={() => {
                    textareaRef.current?.focus();
                    void textComposer.insertText({ text: '@' });
                  }}
                />
              )}
              <SimpleAttachmentSelector />
            </div>
            <SendButton sendMessage={handleSubmit} />
          </div>
        </div>
      </div>
    </WithDragAndDropUpload>
  );
};
